/* global SideDrawer */

if (!customElements.get('quick-add-drawer')) {
  class QuickAddDrawer extends SideDrawer {
    constructor() {
      super();
      this.content = this.querySelector('.js-product-details');
      this.footer = this.querySelector('.drawer__footer');
      this.footerContent = this.querySelector('.js-footer-content');
      this.notification = this.querySelector('.js-added-to-cart');
      this.quoteNotification = this.querySelector('.js-added-to-quote');
      this.backBtn = this.querySelector('.drawer__back-btn');
      this.openCartDrawerLinks = this.querySelectorAll('.js-open-cart-drawer');
      this.cartDrawer = document.querySelector('cart-drawer');
      this.fetch = null;
      this.fetchedUrls = [];
      this.quickAddButtonMouseEnterHandler = this.handleQuickAddButtonMouseEnter.bind(this);
      this.documentClickHandler = this.handleDocumentClick.bind(this);

      document.addEventListener('click', this.documentClickHandler);
      this.addEventListener('on:variant:change', this.handleVariantChange.bind(this));

      this.openCartDrawerLinks.forEach((link) => {
        link.addEventListener('click', this.handleOpenCartClick.bind(this));
      });

      if (theme.device.hasHover && theme.mediaMatches.md) {
        document.querySelectorAll('.js-quick-add').forEach((button) => {
          this.bindQuickAddButtonMouseEnter(button);
        });

        if ('MutationObserver' in window) {
          this.observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
              // Add event listener to new buttons
              mutation.addedNodes.forEach((node) => {
                if (node.nodeType === Node.ELEMENT_NODE) {
                  node.querySelectorAll('.js-quick-add').forEach((button) => {
                    this.bindQuickAddButtonMouseEnter(button);
                  });
                }
              });

              // Remove event listener from removed buttons
              mutation.removedNodes.forEach((node) => {
                if (node.nodeType === Node.ELEMENT_NODE) {
                  node.querySelectorAll('.js-quick-add').forEach((button) => {
                    button.removeEventListener('mouseenter', this.quickAddButtonMouseEnterHandler);
                  });
                }
              });
            });
          });

          // Start observing changes to the DOM
          this.observer.observe(document.body, { childList: true, subtree: true });
        }
      }
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.documentClickHandler);
      document.querySelectorAll('.js-quick-add').forEach((button) => {
        button.removeEventListener('mouseenter', this.quickAddButtonMouseEnterHandler);
      });
      if (this.observer) this.observer.disconnect();
    }

    /**
     * Bind a mouseenter event to a given quick add button if it's not already bound
     * @param {Element} button - The button element to bind the mouseenter event to.
     */
    bindQuickAddButtonMouseEnter(button) {
      if (!button.dataset.quickAddListenerAdded) {
        button.dataset.quickAddListenerAdded = 'true';
        button.addEventListener('mouseenter', this.quickAddButtonMouseEnterHandler);
      }
    }

    /**
     * Start the fetch on hover of a quick add button for perceived performance gains
     * @param {object} evt - Event object.
     */
    handleQuickAddButtonMouseEnter(evt) {
      if (!this.fetchedUrls.includes(evt.target.dataset.productUrl)) {
        this.fetch = {
          url: evt.target.dataset.productUrl,
          promise: fetch(evt.target.dataset.productUrl)
        };
        this.fetchedUrls.push(evt.target.dataset.productUrl);
      }
    }

    /**
     * Handles 'click' events on success notification's 'View cart' link
     * @param {object} evt - Event object.
     */
    handleOpenCartClick(evt) {
      // Open the cart drawer if available on the page
      if (this.cartDrawer) {
        evt.preventDefault();
        this.cartDrawer.open();
      } else if (window.location.pathname === theme.routes.cart) {
        evt.preventDefault();
        this.close();
      }
    }

    /**
     * Handles 'click' events on the document.
     * @param {object} evt - Event object.
     */
    handleDocumentClick(evt) {
      if (!evt.target.matches('.js-quick-add')) return;

      // Close the cart drawer if it's open
      if (this.cartDrawer && this.cartDrawer.ariaHidden === 'false') {
        const overlay = document.querySelector('.js-overlay.is-visible');
        if (overlay) overlay.style.transitionDelay = '200ms';

        this.cartDrawer.close();

        // Wait a few ms for a more optimal ux/animation
        setTimeout(() => {
          this.backBtn.hidden = false;
          this.open(evt.target);
          if (overlay) overlay.style.transitionDelay = '';
        }, 200);
      } else {
        this.open(evt.target);
      }
    }

    /**
     * Handles 'on:variant:change' events on the Quick Add drawer.
     * @param {object} evt - Event object.
     */
    handleVariantChange(evt) {
      let url = this.productUrl;

      if (evt.detail.variant) {
        const separator = this.productUrl.split('?').length > 1 ? '&' : '?';
        url += `${separator}variant=${evt.detail.variant.id}`;
      }

      this.querySelectorAll('.js-prod-link').forEach((link) => {
        link.href = url;
      });

      if (evt.detail.variant && evt.detail.variant.featured_media) {
        this.updateMedia(evt.detail.variant.featured_media);
      }
    }

    /**
     * Opens the drawer and fetches the product details.
     * @param {Element} opener - Element that triggered opening of the drawer.
     */
    async open(opener) {
      opener.setAttribute('aria-disabled', 'true');

      if (this.notification) this.notification.hidden = true;
      if (this.quoteNotification) this.quoteNotification.hidden = true;

      // If it's the same product as previously shown, there's no need to re-fetch the details.
      if (this.productUrl && this.productUrl === opener.dataset.productUrl) {
        super.open(opener);
        opener.removeAttribute('aria-disabled');
        return;
      }

      this.productUrl = opener.dataset.productUrl;
      this.content.innerHTML = '';
      this.classList.add('is-loading');
      this.content.classList.add('drawer__content--out');
      this.footer.classList.add('drawer__footer--out');

      super.open(opener);

      if (!this.fetch || this.fetch.url !== opener.dataset.productUrl) {
        this.fetch = {
          url: opener.dataset.productUrl,
          promise: fetch(opener.dataset.productUrl)
        };
      }

      const response = await this.fetch.promise;
      if (response.ok) {
        const tmpl = document.createElement('template');
        const html = await response.text();
        tmpl.innerHTML = html;
        
        // MODIFIED_BY_RYAN: Extract MOQ data from fetched product page
        const moqMatch = html.match(/window\.BSF_VariantMOQ\[(\d+)\]\s*=\s*(\{[^}]+\})/);
        if (moqMatch) {
          try {
            const productId = moqMatch[1];
            const moqData = JSON.parse(moqMatch[2].replace(/'/g, '"'));
            window.BSF_VariantMOQ = window.BSF_VariantMOQ || {};
            window.BSF_VariantMOQ[productId] = moqData;
            console.log('MOQ data extracted:', productId, moqData);
          } catch (e) {
            console.warn('MOQ extraction failed:', e);
          }
        }
        
        this.productEl = tmpl.content.getElementById('quick-buy-template').content.querySelector('.js-product');
        this.renderProduct(opener);
      }

      this.fetch = null;

      opener.removeAttribute('aria-disabled');
    }

    /**
     * Closes the cart drawer.
     */
    close() {
      super.close(() => {
        this.backBtn.hidden = true;
      });
    }

/**
 * Renders the product details.
 * @param {Element} opener - Element that triggered opening of the drawer.
 */
renderProduct(opener) {
  QuickAddDrawer.convertToQuickBuyContent(this.productEl, this.productUrl);

  // Check whether to select the first variant.
  const variantPicker = this.productEl.querySelector('variant-picker');
  if (variantPicker) {
    this.selectFirstVariant = variantPicker.dataset.selectFirstVariant === 'true';
  }

  this.updateFooter();
  this.updateContent();

  // MODIFIED_BY_RYAN: Initialize MOQ for quick-add drawer
  this.initDrawerMOQ();

  if (opener.dataset.selectedColor && this.selectFirstVariant) {
    // Timeout to allow the VariantPicker to initialize
    setTimeout(this.setActiveVariant.bind(this, opener), 10);
  }
}

/**
 * MODIFIED_BY_RYAN: Initialize MOQ functionality for the quick-add drawer
 */
initDrawerMOQ() {
  setTimeout(() => {
    console.log('initDrawerMOQ: Starting (after delay)...');
    
    const drawer = document.querySelector('quick-add-drawer');
    if (!drawer) {
      console.log('MOQ: No drawer found');
      return;
    }
    
    // Find the MOQ map
    let moqMap = null;
    if (window.BSF_VariantMOQ) {
      const firstProductId = Object.keys(window.BSF_VariantMOQ)[0];
      if (firstProductId) {
        moqMap = window.BSF_VariantMOQ[firstProductId];
      }
    }

    // Read unit plural from rendered price in the drawer
    let moqUnitLabel = '';
    const priceEl = drawer.querySelector('.price[data-unit-plural]');
    if (priceEl && priceEl.dataset.unitPlural) {
      moqUnitLabel = priceEl.dataset.unitPlural;
    }
    
    if (!moqMap) {
      console.log('MOQ: No MOQ map found');
      return;
    }
    
    console.log('MOQ: Found moqMap, setting up event listener');
    
    let lastVariantId = null;
    let userIncreasedQty = false;
    
    function isManualQty(currentQty) {
      if (currentQty === 1) return false;
      const moqValues = Object.values(moqMap);
      for (let i = 0; i < moqValues.length; i++) {
        if (parseInt(moqValues[i], 10) === currentQty) return false;
      }
      return true;
    }
    
    function createLabel(qtyInput) {
      let label = qtyInput.querySelector('.moq-label');
      if (!label) {
        label = document.createElement('div');
        label.className = 'moq-label';
        // Adjust positioning to be a bit lower
        label.style.bottom = 'calc(100% - 4px)';
        qtyInput.appendChild(label);
      }
      return label;
    }
    
    function updateLabel(form, moq, currentQty) {
      const qtyInput = form.querySelector('quantity-input');
      if (!qtyInput) return;
      
      const shouldShow = moq > 1 && currentQty === moq;
      const label = createLabel(qtyInput);
      
      if (shouldShow) {
        label.textContent = 'MINIMUM QTY: ' + moq + (moqUnitLabel ? ' ' + moqUnitLabel : '');
      }
      
      requestAnimationFrame(() => {
        if (shouldShow) {
          label.classList.add('is-visible');
        } else {
          label.classList.remove('is-visible');
        }
      });
    }
    
    function applyMOQ(isVariantChange) {
      // Get fresh references every time since elements get replaced
      const form = document.getElementById('product-form-quickadd');
      if (!form) return;
      
      const idInput = form.querySelector('input[name="id"]');
      const qty = document.getElementById('quantity-quickadd');
      
      if (!idInput || !qty) return;
      
      const variantId = idInput.getAttribute('value') || idInput.value;
      const currentMoq = moqMap[variantId] ? parseInt(moqMap[variantId], 10) : 1;
      const currentQty = parseInt(qty.value, 10);
      const variantChanged = lastVariantId !== null && lastVariantId !== variantId;
      
      console.log('MOQ applyMOQ: variantId=', variantId, 'moq=', currentMoq, 'qty=', currentQty, 'variantChanged=', variantChanged, 'userIncreasedQty=', userIncreasedQty);
      
      qty.min = String(currentMoq);
      
      if (variantChanged || isVariantChange) {
        // Check if qty was manually increased by user
        const manuallySet = isManualQty(currentQty);
        
        if (currentQty < currentMoq) {
          // Current qty is below new MOQ, must increase
          qty.value = String(currentMoq);
          userIncreasedQty = false;
        } else if (manuallySet || userIncreasedQty) {
          // User manually increased qty, keep it
          console.log('MOQ: Keeping user-set qty');
        } else {
          // Not manually set, reset to new MOQ
          qty.value = String(currentMoq);
          userIncreasedQty = false;
        }
      } else if (currentQty < currentMoq) {
        qty.value = String(currentMoq);
      }
      
      lastVariantId = variantId;
      
      const finalQty = parseInt(qty.value, 10);
      updateLabel(form, currentMoq, finalQty);
    }
    
    // Listen for the variant change event that fires AFTER content is updated
    drawer.addEventListener('on:variant:change', (evt) => {
      console.log('MOQ: on:variant:change event received');
      setTimeout(() => applyMOQ(true), 50);
    });
    
    // Track when user manually changes quantity
    drawer.addEventListener('click', (evt) => {
      if (evt.target.closest('.qty-input__btn')) {
        setTimeout(() => {
          const qty = document.getElementById('quantity-quickadd');
          const form = document.getElementById('product-form-quickadd');
          const idInput = form?.querySelector('input[name="id"]');
          
          if (qty && idInput) {
            const variantId = idInput.getAttribute('value') || idInput.value;
            const currentMoq = moqMap[variantId] ? parseInt(moqMap[variantId], 10) : 1;
            const currentQty = parseInt(qty.value, 10);
            
            if (currentQty > currentMoq) {
              userIncreasedQty = true;
              console.log('MOQ: User increased qty above MOQ');
            } else {
              userIncreasedQty = false;
            }
            
            updateLabel(form, currentMoq, currentQty);
          }
        }, 50);
      }
    });
    
    // Apply on initial load
    applyMOQ(false);
    
    console.log('MOQ: Drawer MOQ initialized with on:variant:change listener');
    
  }, 200);
}











    /**
     * Set color variant to match the one selected in the card.
     * @param {Element} opener - Element that triggered opening of the drawer.
     */
    setActiveVariant(opener) {
      const colorOptionBox = this.querySelector(`.opt-btn[value="${opener.dataset.selectedColor}"]`);
      if (colorOptionBox) {
        this.querySelector(`.opt-btn[value="${opener.dataset.selectedColor}"]`)
          .click();
      } else {
        const colorOptionDropdown = this.querySelector(
          `.custom-select__option[data-value="${opener.dataset.selectedColor}"]`
        );
        if (colorOptionDropdown) {
          const customSelect = colorOptionDropdown.closest('custom-select');
          customSelect.selectOption(colorOptionDropdown);
        }
      }
    }

    /**
     * Updates the product media.
     * @param {string} media - Variant featured media object.
     */
    updateMedia(media) {
      if (!media) return;

      const { src } = media.preview_image;
      const container = this.querySelector('.quick-add-info__media');
      const width = container.offsetWidth;
      const aspectRatio = media.preview_image.aspect_ratio;

      const existingId = container.dataset.mediaId;
      const existingImg = container.querySelector('img');

      if (!existingImg || existingId !== `${media.id}`) {
        container.innerHTML = `
          <img src="${src}&width=${width}" srcset="${src}&width=${width}, ${src}&width=${width * 2} 2x" width="${width * 2}" height="${(width * 2) / aspectRatio}" alt="${media.alt || ''}">
        `;
        container.dataset.mediaId = media.id;
      }
    }

    /**
     * Renders the markup for the drawer content element.
     */
    updateContent() {
      const productContent = this.productEl.querySelector('.js-product-details');
      if (productContent) {
        this.content.innerHTML = productContent.innerHTML;
      }

      this.classList.remove('is-loading');
      this.content.classList.remove('drawer__content--out');
    }

    /**
     * Renders the markup for the drawer footer element.
     */
    updateFooter() {
      const footerContent = this.productEl.querySelector('.js-footer-content');
      this.footer.classList.remove('quick-add__footer-message');

      if (footerContent && footerContent.hasChildNodes()) {
        this.footerContent.innerHTML = footerContent.innerHTML;
      } else {
        this.footer.classList.add('quick-add__footer-message');
        this.footerContent.innerHTML = `
          <div class="alert quick-add__alert bg-info-bg text-info-text">
            <div class="flex">
              <div>
                <svg class="icon icon--price_tag" width="32" height="32" viewBox="0 0 16 16" aria-hidden="true" focusable="false" role="presentation">
                  <path fill="currentColor" d="M7.59 1.34a1 1 0 01.7-.29h5.66a1 1 0 011 1v5.66a1 1 0 01-.3.7L7.6 15.5a1 1 0 01-1.42 0L.52 9.83a1 1 0 010-1.42l7.07-7.07zm6.36 6.37l-7.07 7.07-5.66-5.66L8.3 2.05h5.66v5.66z" fill-rule="evenodd"/>
                  <path fill="currentColor" d="M9.7 6.3a1 1 0 101.42-1.42 1 1 0 00-1.41 1.41zM9 7a2 2 0 102.83-2.83A2 2 0 009 7z" fill-rule="evenodd"/>
                </svg>
              </div>
              <div>
                <p class="text-h6 font-bold mb-2">${theme.strings.awaitingSale}</p>
                <a class="link js-prod-link" href="${this.productUrl}">${theme.strings.viewDetails}</a>
              </div>
            </div>
          </div>`;
      }

      this.footer.classList.remove('drawer__footer--out');
    }

    /**
     * Shows an "Added to cart" message in the drawer.
     */
    addedToCart() {
      if (this.notification) {
        setTimeout(() => {
          this.notification.hidden = false;
        }, 300);

        setTimeout(() => {
          this.notification.hidden = true;
        }, this.notification.dataset.visibleFor);
      }
    }

    /* CUSTOM_RFQ_BY_CLAUDE — "Added to quote" notification in quick buy drawer */
    /**
     * Shows an "Added to quote" message in the drawer.
     */
    addedToQuote() {
      if (this.quoteNotification) {
        setTimeout(() => {
          this.quoteNotification.hidden = false;
        }, 300);

        setTimeout(() => {
          this.quoteNotification.hidden = true;
        }, this.quoteNotification.dataset.visibleFor);
      }
    }
    /* END CUSTOM_RFQ_BY_CLAUDE */

    /**
     * Convert product template content to quick buy content.
     * @param {Element} container - Container element containing new content to use.
     */
    static convertToQuickBuyContent(container) {
      // Prevent variant picker from updating the URL on change.
      const variantPicker = container.querySelector('variant-picker');
      if (variantPicker) {
        variantPicker.dataset.updateUrl = 'false';
      }

      // Remove size chart modal and link (if they exist).
      const sizeChartModal = container.querySelector('[data-modal="size-chart"]');
      if (sizeChartModal) {
        sizeChartModal.remove();
      }
    }
  }

  customElements.define('quick-add-drawer', QuickAddDrawer);

  window.theme = window.theme || {};
  window.theme.quickBuy = window.theme.quickBuy || {};
  window.theme.quickBuy.convertToQuickBuyContent = QuickAddDrawer.convertToQuickBuyContent;
}